import { api } from '../../services/api';
import { comNoCache } from './utils/cacheTempoReal';
import type { PlanilhaDoFluxo } from './utils/exportarFluxo';

/**
 * Client do prefixo NOVO `/api/v1/orquestrador` (contrato de 03/09).
 *
 * E' a fonte que substituiu, bloco a bloco, as rotas legadas que a v1 da pagina
 * consumia: o BFF (especialistas-backend) e' o unico dono do join
 * Mongo+Postgres, e o front NUNCA fala direto com o monitoramento-bot-api. Os tipos
 * abaixo sao fieis aos `resposta_exemplo` do contrato — mudou o shape la', muda aqui.
 */

const BASE = '/api/v1/orquestrador';

// Controle do cache "tempo real" (05/10): mora em `utils/cacheTempoReal` porque
// sao funcoes PURAS e este arquivo importa o axios, que o Jest do CRA nao
// transpila. Reexportado abaixo para quem ja' importa de `../api`.
export { STALE_TEMPO_REAL, comNoCache } from './utils/cacheTempoReal';

interface IEnvelope<T> {
  data: T;
}

/** Status canonico da esteira — mesma chave em TODA resposta do prefixo. */
/**
 * `skip` (24/09): a empresa foi PULADA porque nao havia trabalho a fazer (sem
 * movimento, sem tributo). Nao e' sucesso nem falha — e' ausencia de obrigacao.
 *
 * O `skip` cuja natureza e' "ja' estava feita" chega como `done` (a obrigacao
 * ESTA' cumprida) e o de bloqueio de cadastro chega como `err` (trabalho travado
 * esperando gente); o backend resolve isso em `natureza_skip`. Aqui so' sobra o
 * caso "nao havia o que fazer".
 */
export type StatusEsteira = 'done' | 'skip' | 'err' | 'warn' | 'run' | 'wait';

/** Contagem por status de um no' (grupo ou bot). Sempre as 5 chaves, zeradas se preciso. */
export interface IContagens {
  done: number;
  err: number;
  warn: number;
  run: number;
  wait: number;
  /**
   * Empresas PULADAS por falta de trabalho (24/09). O backend ja' publica o
   * status `skip`; sem o campo aqui elas caiam em nenhuma contagem e o total do
   * no' nao fechava com `lista.length`.
   */
  skip: number;
}

/** Recorte de periodo (ISO) que os endpoints cacheados aceitam. */
export interface IPeriodoParams {
  date_init?: string;
  date_end?: string;
}

// ── Execucoes ────────────────────────────────────────────────────────────────

export interface IExecucaoAtiva {
  exec_id: string;
  bot: string;
  tipo: string;
  total: number;
  done: number;
  pct: number;
  eta_s: number | null;
  atual: string | null;
  ok: number;
  erro: number;
  maquina: string | null;
  iniciada_em: string;
  travado: boolean;
}

export interface IUnidadeRecente {
  codi_emp: string;
  status: string;
  motivo: string | null;
  ts: string;
}

export interface IExecucaoDetalhe {
  exec_id: string;
  bot: string;
  tipo: string;
  competencia: string | null;
  total: number;
  done: number;
  ok: number;
  erro: number;
  pulado: number;
  falha_op: number;
  aguarda: number;
  ok_empresas: string[];
  erro_empresas: string[];
  pulado_empresas: string[];
  falha_op_empresas: string[];
  aguarda_empresas: string[];
  aguardando_na_fila: string[];
  estado: string;
  iniciada_em: string | null;
  finalizada_em: string | null;
  throughput_por_min: number | null;
  ao_vivo: {
    pct: number;
    eta_s: number | null;
    atual: string | null;
    done: number;
  } | null;
  ultimas_unidades: IUnidadeRecente[];
}

/** Situacao de um modulo do catalogo no nivel nano. */
export type SituacaoModulo = 'concluido' | 'em_andamento' | 'pendente';

export interface IModuloNano {
  modulo_id: number;
  /** O catalogo pode nao ter nome — render usa fallback "Módulo {modulo_id}". */
  nome: string | null;
  declarado: boolean;
  situacao: SituacaoModulo;
  passagens: number;
  duracao_ms: number | null;
  desde: string | null;
  ultimo_fim: string | null;
}

export interface INanoResposta {
  modulos: IModuloNano[];
  /**
   * Posicao atual (maior modulo iniciado sem concluido), ou null — OBJETO, como o
   * diagnostico_service.posicao_atual devolve, nunca o modulo_id solto.
   */
  atual: { modulo_id: number; codi_emp: string | null; desde: string | null } | null;
}

export interface IPosicaoAtual {
  modulo_id: number;
  codi_emp: string | null;
  desde: string | null;
}

/** Cadastro da empresa enriquecido de tb_empresa (chave = external_id = codi_emp). */
export interface IEmpresaInfo {
  external_id: string;
  nome: string | null;
  razao_social: string | null;
  cnpj: string | null;
  uf: string | null;
  regime: string | null;
  is_simples_nacional: boolean | null;
  grupo_de_empresas: string | null;
}

export interface IFilaPendentes {
  aguardando: string[];
  em_processamento: string[];
  concluidas: string[];
  total: number;
  codi_emps: string[];
  empresas: Record<string, IEmpresaInfo>;
}

/** Mapa codi_emp → { area: responsavel } declarado PELO BOT (nunca de tb_empresa). */
export type IResponsaveisPorEmpresa = Record<string, Record<string, string>>;

export const getExecucoesAtivas = async (): Promise<IExecucaoAtiva[]> => {
  const res = await api.get<IEnvelope<IExecucaoAtiva[]>>(`${BASE}/execucoes/ativas`);
  return res.data.data;
};

export const getExecucaoDetalhe = async (execId: string): Promise<IExecucaoDetalhe> => {
  const res = await api.get<IEnvelope<IExecucaoDetalhe>>(
    `${BASE}/execucoes/${encodeURIComponent(execId)}`,
  );
  return res.data.data;
};

export const getExecucaoNano = async (execId: string, codiEmp?: string): Promise<INanoResposta> => {
  const res = await api.get<IEnvelope<INanoResposta>>(
    `${BASE}/execucoes/${encodeURIComponent(execId)}/nano`,
    { params: codiEmp ? { codi_emp: codiEmp } : {} },
  );
  return res.data.data;
};

export const getExecucaoPosicao = async (
  execId: string,
  codiEmp?: string,
): Promise<IPosicaoAtual> => {
  const res = await api.get<IEnvelope<IPosicaoAtual>>(
    `${BASE}/execucoes/${encodeURIComponent(execId)}/posicao`,
    { params: codiEmp ? { codi_emp: codiEmp } : {} },
  );
  return res.data.data;
};

export const getExecucaoFilaPendentes = async (execId: string): Promise<IFilaPendentes> => {
  const res = await api.get<IEnvelope<IFilaPendentes>>(
    `${BASE}/execucoes/${encodeURIComponent(execId)}/fila/pendentes`,
  );
  return res.data.data;
};

export const getExecucaoResponsaveis = async (
  execId: string,
  codiEmp?: string,
): Promise<IResponsaveisPorEmpresa> => {
  const res = await api.get<IEnvelope<Record<string, unknown>>>(
    `${BASE}/execucoes/${encodeURIComponent(execId)}/responsaveis`,
    { params: codiEmp ? { codi_emp: codiEmp } : {} },
  );
  // O backend responde por empresa o envelope { areas, fonte: "espelho"|"bot" }
  // (fonte primária = espelho do BFF; o mapa do bot é fallback). Normaliza aqui,
  // na fronteira, para o mapa plano que toda a tela consome — tolerando também o
  // shape antigo (mapa direto) durante janela de deploy, e descartando valores
  // não-string (área sem responsável vem null e não é opção de filtro).
  const plano: IResponsaveisPorEmpresa = {};
  Object.entries(res.data.data ?? {}).forEach(([codi, valor]) => {
    const mapa =
      valor && typeof valor === 'object' && 'areas' in (valor as Record<string, unknown>)
        ? (valor as { areas?: unknown }).areas
        : valor;
    if (!mapa || typeof mapa !== 'object') return;
    const limpo: Record<string, string> = {};
    Object.entries(mapa as Record<string, unknown>).forEach(([area, nome]) => {
      if (typeof nome === 'string' && nome) limpo[area] = nome;
    });
    if (Object.keys(limpo).length) plano[codi] = limpo;
  });
  return plano;
};

// ── Resumo (1 linha por empresa) ─────────────────────────────────────────────

export type SituacaoEmpresa = 'Sem registros' | 'Concluída' | 'Em falha' | 'Em andamento';

export interface IBotDaEmpresa {
  bot: string;
  tipo_bot: string | null;
  status: StatusEsteira;
  motivo_erro: string | null;
  motivo_aviso: string | null;
  tem_screenshot: boolean;
  /**
   * Quantas EXECUCOES falharam na janela, mesmo que a empresa tenha concluido
   * depois — e' HISTORICO, nunca status (o status vem do ultimo desfecho).
   *
   * Existe para o selo "ja' falhou Nx" na empresa que hoje esta' concluida: sem
   * ele, corrigir o status esconderia que houve problema. `null` = o bot nao
   * reporta por unidade (10 de 22 reportam).
   */
  falhas_no_periodo?: number | null;
  /**
   * Texto do ULTIMO skip do par — so' vem preenchido quando o ultimo desfecho e'
   * `skip` (mesma regra de `natureza_skip`). E' o que a caixa da Pendente exibe.
   *
   * Campo PROPRIO, e nao `motivo_erro`, porque medido em producao (setembro/2026)
   * 251 dos 1276 pares em skip ja' chegam com `motivo_erro` preenchido pelo texto
   * de uma falha ANTIGA: reusar o campo mostraria erro velho como motivo da
   * pendencia.
   *
   * Opcional por retrocompat (BFF antigo, resposta em cache): ausente, `null` e
   * string vazia significam a mesma coisa.
   */
  motivo_skip?: string | null;
  /**
   * Quando foi a ultima EXECUCAO do par que terminou sem processar, dentro da
   * janela — vem sempre que houve falha, qualquer que seja o status de hoje.
   * ISO-8601 SEM fuso, horario de Sao Paulo ("2026-09-14T04:49:22"): ler com
   * `lerDataFalha`, nunca com `Date`.
   */
  ultima_falha_em?: string | null;
  /** Texto daquela mesma falha. Vem junto de `ultima_falha_em`, ou os dois nulos. */
  ultima_falha_motivo?: string | null;
}

export interface IEmpresaResumo {
  codi_emp: string;
  empresa: IEmpresaInfo | null;
  /**
   * Flags de vigência (pedido 08/09): o BFF parou de EXCLUIR do /resumo as empresas
   * inativas/fora da janela — elas chegam marcadas e a exclusão virou regra de
   * EXIBIÇÃO do front (default esconde). Quem revela é o filtro de SITUAÇÃO
   * CADASTRAL da aba Resumo (pedido 09/09: "Empresas ativas" | "Empresas inativas" |
   * "Ativas e inativas") — o "Ver todas as empresas" do banner deixou de revelá-las
   * e hoje só ignora o recorte de PERÍODO. Opcionais para retrocompat: BFF antigo
   * sem os campos = empresa visível como sempre foi.
   */
  ativa?: boolean;
  fora_vigencia?: boolean;
  /**
   * Área essencial → responsável, do CADASTRO (contrato 08/09): só as 3 áreas
   * essenciais (contabil/fiscal/pessoal) e só as PREENCHIDAS. É a fonte PRIMÁRIA
   * das células/filtros Responsável/Área do Resumo — cobre também empresa sem
   * execução ativa (que antes mostrava travessão); ausente (BFF antigo, ainda sem
   * o campo) → fallback no mapa das execuções ativas, comportamento de sempre.
   */
  areas?: Record<string, string>;
  situacao: SituacaoEmpresa;
  posicao: { bot: string; status: StatusEsteira } | null;
  bots_total: number;
  bots_concluidos: number;
  bots_com_falha: number;
  avisos: number;
  ultima_atividade: string | null;
  bots: IBotDaEmpresa[];
}

export interface IResumoResposta {
  empresas: IEmpresaResumo[];
  total: number;
  /** De quando são os dados do monitoramento (ISO). Ausente em BFF antigo. */
  calculado_em?: string | null;
}

export const getResumo = async (periodo: IPeriodoParams = {}): Promise<IResumoResposta> => {
  const res = await api.get<IEnvelope<IResumoResposta>>(`${BASE}/resumo`, {
    params: comNoCache(periodo),
  });
  return res.data.data;
};

// ── Esteira (board macro) e board de grupo ───────────────────────────────────

export interface IGrupoEsteira {
  /** uuid4 hex (bot_organizacao); null so' no grupo virtual "Bots sem grupo". */
  id: string | null;
  nome: string;
  depende_de: string | null;
  departamento_id: string | null;
  departamento: string | null;
  bots: string[];
  total_empresas: number;
  contagens: IContagens;
}

export interface ILinhaEsteira {
  /** Departamento do PRIMEIRO grupo da cadeia; null = "Sem departamento vinculado". */
  departamento: string | null;
  grupos: IGrupoEsteira[];
}

export interface IEsteiraResposta {
  linhas: ILinhaEsteira[];
  calculado_em?: string | null;
}

export interface IBotBoard {
  bot: string;
  tipo_bot: string;
  dep_bot: string | null;
  total_empresas: number;
  contagens: IContagens;
  ultima_execucao: string | null;
  em_execucao: boolean;
  exec_id: string | null;
}

export interface IGrupoBoardResposta {
  grupo: {
    id: string;
    nome: string;
    depende_de: string | null;
    departamento_id: string | null;
    departamento: string | null;
  };
  bots: IBotBoard[];
}

export const getEsteira = async (periodo: IPeriodoParams = {}): Promise<IEsteiraResposta> => {
  const res = await api.get<IEnvelope<IEsteiraResposta>>(`${BASE}/esteira`, {
    params: comNoCache(periodo),
  });
  return res.data.data;
};

export const getGrupoBoard = async (
  grupoId: string,
  periodo: IPeriodoParams = {},
): Promise<IGrupoBoardResposta> => {
  const res = await api.get<IEnvelope<IGrupoBoardResposta>>(
    `${BASE}/grupos/${encodeURIComponent(grupoId)}/board`,
    { params: comNoCache(periodo) },
  );
  return res.data.data;
};

// ── Dependencias (config ao vivo — sem cache no BFF) ─────────────────────────

export interface IGrupoBot {
  /** uuid4 hex gerado pelo mon (bot_organizacao) — id e' STRING, nunca number. */
  id: string;
  nome: string;
  depende_de: string | null;
  departamento_id: string | null;
  /**
   * REGIME POR NÍVEL (contrato 08/09): rótulo normalizado do BFF (mesmos labels do
   * /resumo, ex. "Simples Nacional") ou null = herda do departamento. Opcional para
   * retrocompat com BFF que ainda não publica o campo (ausente = null = herda).
   */
  regime?: string | null;
}

export interface IDepartamento {
  /** uuid4 hex gerado pelo mon (bot_organizacao). */
  id: string;
  nome: string;
  /** Regime por nível (contrato 08/09) — topo da herança; null = sem restrição. */
  regime?: string | null;
  /**
   * Posição do fluxo na aba Processos (13/10) — GLOBAL, menor primeiro. Null ou
   * ausente (backend antigo) = sem posição; a tela aplica a ordem padrão.
   */
  ordem?: number | null;
}

export interface IVinculoBot {
  bot: string;
  /**
   * Apelido de EXIBIÇÃO (bot_configs.apelido — mesmo campo que o monitoramento já
   * usa). Null = sem apelido, a UI mostra o nome real; nunca substitui o nome real
   * em chave/filtro/navegação.
   */
  apelido: string | null;
  /** Grupos do bot (uuid4 hex de bot_organizacao) — lista vazia = "Bots sem grupo". */
  grupo_ids: string[];
  dep_bot: string | null;
  /**
   * SOFT-DELETE (pedido 08/09, bots fantasmas do auto-cadastro legado): timestamp
   * ISO da inativação, null/ausente = ativo. O item só vem com o campo preenchido
   * na listagem com `incluir_inativados=true`; bot inativado NÃO aparece em
   * nenhuma leitura padrão da tela.
   */
  inativado_em?: string | null;
  /**
   * REGIME POR NÍVEL (contrato 08/09): rótulo normalizado do BFF ou null = herda
   * grupo → departamento (o mais específico vence). Opcional para retrocompat.
   */
  regime?: string | null;
}

export const listarGrupos = async (): Promise<IGrupoBot[]> => {
  const res = await api.get<IEnvelope<IGrupoBot[]>>(`${BASE}/dependencias/grupos`);
  return res.data.data;
};

export const criarGrupo = async (payload: {
  nome: string;
  depende_de?: string | null;
  departamento_id?: string | null;
}): Promise<IGrupoBot> => {
  const res = await api.post<IEnvelope<IGrupoBot>>(`${BASE}/dependencias/grupos`, payload);
  return res.data.data;
};

/**
 * Atualizacao parcial; `null` explicito LIMPA o campo (contrato). Ciclo → 400 do mon.
 * `regime: null` limpa o regime do grupo (volta a herdar do departamento).
 */
export const atualizarGrupo = async (
  grupoId: string,
  payload: Partial<{
    nome: string;
    depende_de: string | null;
    departamento_id: string | null;
    regime: string | null;
  }>,
): Promise<IGrupoBot> => {
  const res = await api.patch<IEnvelope<IGrupoBot>>(
    `${BASE}/dependencias/grupos/${encodeURIComponent(grupoId)}`,
    payload,
  );
  return res.data.data;
};

export interface IGrupoExcluido {
  removido: boolean;
  bots_desvinculados: number;
  grupos_com_dependencia_limpa: number;
}

/** Exclui SEM apagar bots: o mon remove o id de `grupo_ids` dos bots e limpa `depende_de`. */
export const excluirGrupo = async (grupoId: string): Promise<IGrupoExcluido> => {
  const res = await api.delete<IEnvelope<IGrupoExcluido>>(
    `${BASE}/dependencias/grupos/${encodeURIComponent(grupoId)}`,
  );
  return res.data.data;
};

export const listarDepartamentos = async (): Promise<IDepartamento[]> => {
  const res = await api.get<IEnvelope<IDepartamento[]>>(`${BASE}/dependencias/departamentos`);
  return res.data.data;
};

export const criarDepartamento = async (payload: { nome: string }): Promise<IDepartamento> => {
  const res = await api.post<IEnvelope<IDepartamento>>(
    `${BASE}/dependencias/departamentos`,
    payload,
  );
  return res.data.data;
};

/** Atualizacao parcial; `regime: null` limpa o regime do departamento. */
export const atualizarDepartamento = async (
  departamentoId: string,
  payload: Partial<{ nome: string; regime: string | null; ordem: number | null }>,
): Promise<IDepartamento> => {
  const res = await api.patch<IEnvelope<IDepartamento>>(
    `${BASE}/dependencias/departamentos/${encodeURIComponent(departamentoId)}`,
    payload,
  );
  return res.data.data;
};

export interface IDepartamentoExcluido {
  removido: boolean;
  grupos_desvinculados: number;
}

export const excluirDepartamento = async (
  departamentoId: string,
): Promise<IDepartamentoExcluido> => {
  const res = await api.delete<IEnvelope<IDepartamentoExcluido>>(
    `${BASE}/dependencias/departamentos/${encodeURIComponent(departamentoId)}`,
  );
  return res.data.data;
};

/**
 * Sem o param, o backend já filtra os inativados NA FONTE; o client refiltra por
 * `inativado_em` DEFENSIVAMENTE — assim bot inativado não aparece em nenhum
 * consumidor da tela (listas, selects de "Depende de", boards, apelidos), mesmo
 * se algum backend antigo devolver o item. `incluirInativados=true` é só para a
 * seção "Bots removidos" da aba Dependências.
 */
export const listarVinculosBots = async (incluirInativados = false): Promise<IVinculoBot[]> => {
  const res = await api.get<IEnvelope<IVinculoBot[]>>(`${BASE}/dependencias/bots`, {
    params: incluirInativados ? { incluir_inativados: true } : {},
  });
  return incluirInativados ? res.data.data : res.data.data.filter(v => !v.inativado_em);
};

/**
 * Soft-delete do bot (contrato 08/09) — o histórico NUNCA é apagado, o bot só some
 * das leituras; `reativarBot` desfaz. Enquanto o backend não publicar as rotas, o
 * 404 cai na CaixaErro padrão do card (sem crash).
 */
export const inativarBot = async (botName: string): Promise<void> => {
  await api.post(`${BASE}/dependencias/bots/${encodeURIComponent(botName)}/inativar`);
};

export const reativarBot = async (botName: string): Promise<void> => {
  await api.post(`${BASE}/dependencias/bots/${encodeURIComponent(botName)}/reativar`);
};

/**
 * Atualização parcial do vínculo: `apelido: null` LIMPA o apelido; `grupo_ids`
 * SUBSTITUI a lista completa (todo id precisa existir — 400 do mon nomeando os
 * inválidos); `dep_bot` mantém a validação de ciclo (400 com a mensagem exibível);
 * `regime: null` limpa o regime do bot (volta a herdar grupo → departamento).
 */
export const atualizarVinculoBot = async (
  botName: string,
  payload: Partial<{
    apelido: string | null;
    grupo_ids: string[];
    dep_bot: string | null;
    regime: string | null;
  }>,
): Promise<IVinculoBot> => {
  const res = await api.patch<IEnvelope<IVinculoBot>>(
    `${BASE}/dependencias/bots/${encodeURIComponent(botName)}`,
    payload,
  );
  return res.data.data;
};

// ── Auditoria de erros (tabela de ocorrências erro/warning da esteira) ───────

/** Tipos de bot aceitos pelo filtro da auditoria — mesmo vocabulário do mon. */
export type TipoBotAuditoria = 'RPA' | 'ETL' | 'WEBSCRAP';

/** Status de uma ocorrência da auditoria (≠ StatusEsteira — vocabulário do log). */
export type StatusAuditoria = 'erro' | 'warning';

/** Fonte da linha da auditoria — ver `IAuditoriaErroItem.origem`. */
export type OrigemAuditoria = 'trilha' | 'unidade';

/** Filtro de status aceito pelo endpoint; default DO SERVIDOR = 'erro'. */
export type StatusAuditoriaFiltro = StatusAuditoria | 'ambos';

/** Bloco `detalhe` da ocorrência — presente só quando o bot mandou o payload rico. */
export interface IAuditoriaErroDetalhe {
  error_code: string | null;
  error_nome: string | null;
  tipo_exception: string | null;
  motivo: string | null;
  modulo_id: number | null;
}

export interface IAuditoriaErroItem {
  id: string;
  /**
   * ISO já em horário de São Paulo (contrato) — formatar os CAMPOS literais,
   * nunca via Date. Null quando o documento da trilha não gravou ts (o mon
   * nunca responde 500 por campo ausente — sai null).
   */
  ts: string | null;
  bot: string;
  tipo_bot: string | null;
  exec_id: string | null;
  codi_emp: string | null;
  nome_empresa: string | null;
  competencia: string | null;
  /** Label canônica da etapa; `etapa_raw` preserva o que o bot mandou cru. */
  etapa: string | null;
  etapa_raw: string | null;
  status: StatusAuditoria;
  tipo_erro: string | null;
  mensagem: string;
  /** true = o servidor cortou a mensagem no armazenamento (a UI sufixa "…"). */
  mensagem_truncada: boolean;
  tem_print: boolean;
  print_key: string | null;
  detalhe: IAuditoriaErroDetalhe | null;
  /**
   * De qual FONTE a linha veio (21/09):
   *
   * - `trilha`  — log que o bot escreveu (`botrpa`/`botetl`/`botwebscrap`);
   * - `unidade` — o fecho da unidade que NÃO processou a empresa
   *   (`bot_unit_result` com status falha_op/retry/aguarda_humano).
   *
   * A segunda fonte existe porque a lib de 09/2026 passou a gravar o fecho da
   * unidade fora da trilha: a aba Processos foi adaptada e a auditoria não,
   * então a empresa aparecia como "Falha" no nó e sumia aqui.
   *
   * A tela usa para explicar a ausência de print — o fecho de unidade só tem
   * print quando há evidência casando (exec_id, empresa), e sem dizer isso a
   * ausência pareceria defeito.
   */
  origem: OrigemAuditoria;
  /** Código do catálogo; só o fecho de unidade carrega (a trilha vem null). */
  error_code: number | null;
  /** Nome do código ("SEM_PROCURACAO") — evita a tela exibir só a frase solta. */
  error_nome: string | null;
}

export interface IAuditoriaResumoTipo {
  tipo_erro: string | null;
  qtd: number;
}

export interface IAuditoriaResumoBot {
  bot: string;
  qtd: number;
}

export interface IAuditoriaErrosResposta {
  total: number;
  limit: number;
  offset: number;
  /** Janela EFETIVA aplicada pelo servidor (clamp de 31 dias — `janela_clampada`). */
  de_efetivo: string;
  /** Null = sem limite superior (a consulta foi "até agora"). */
  ate_efetivo: string | null;
  janela_clampada: boolean;
  itens: IAuditoriaErroItem[];
  resumo: {
    por_tipo: IAuditoriaResumoTipo[];
    por_bot: IAuditoriaResumoBot[];
    /**
     * Empresas DISTINTAS do filtro inteiro (não só da página) — o denominador que
     * traduz "108 ocorrências" em "35 empresas" e desfaz a contradição aparente
     * com o nó de Processos (achado 5 da auditoria 09/09).
     *
     * OPCIONAL de propósito: o campo está sendo adicionado no backend em paralelo
     * e a UI não pode quebrar (nem mentir) enquanto ele não sobe — sem ele a aba
     * cai no cálculo por PÁGINA, que é honesto porque o rótulo diz "nesta página".
     */
    empresas_distintas?: number;
    /** Ocorrências SEM empresa (codi_emp null) no filtro inteiro — mesma regra. */
    sem_empresa?: number;
  };
}

export interface IAuditoriaErrosParams {
  de?: string;
  ate?: string;
  bot?: string;
  tipo_bot?: TipoBotAuditoria;
  codi_emp?: string;
  status?: StatusAuditoriaFiltro;
  etapa?: string;
  /** Busca textual NA MENSAGEM (contrato) — não cobre tipo_erro/bot/empresa. */
  q?: string;
  /** Máx 200 (o servidor corta acima); default do servidor 50. */
  limit?: number;
  offset?: number;
}

export const getAuditoriaErros = async (
  params: IAuditoriaErrosParams = {},
): Promise<IAuditoriaErrosResposta> => {
  const res = await api.get<IEnvelope<IAuditoriaErrosResposta>>(`${BASE}/auditoria/erros`, {
    params,
  });
  return res.data.data;
};

// ── Histórico cronológico de UMA empresa num bot ─────────────────────────────

/**
 * Um BLOCO da linha do tempo: eventos CONSECUTIVOS e iguais do servidor vêm
 * fundidos num só, com `ocorrencias` e o par primeiro/último.
 *
 * Por que blocos, e não eventos: o pior par real medido tem 143 eventos, que
 * colapsam em 5 blocos. Sem o agrupamento a linha do popup viraria uma parede de
 * repetição — e é o que o dono pediu ("junta as linhas em uma só").
 *
 * "Iguais" é a tripla (status, error_code, motivo). CONSECUTIVOS, não "todos os
 * iguais": a empresa que falhou, foi corrigida e voltou a falhar tem DOIS blocos
 * do mesmo motivo, e essa separação é a história.
 */
export interface IHistoricoBloco {
  /** `ok` | `retry` | `skip` | `falha_op` | `aguarda_humano` (nunca null: o
   *  servidor exclui a unidade em voo, que não é desfecho). */
  status: string | null;
  error_code: number | null;
  motivo: string | null;
  funcao: string | null;
  etapa: string | null;
  competencia: string | null;
  tentativas: number | null;
  exec_id: string | null;
  /** Quantos eventos iguais e consecutivos este bloco representa (≥1). */
  ocorrencias: number;
  /** ISO em São Paulo, SEM offset — ler literal com `lerDataFalha`, nunca `Date`. */
  primeiro_em: string | null;
  ultimo_em: string | null;
}

export interface IHistoricoUnidade {
  bot: string;
  codi_emp: string;
  /**
   * As identidades que o servidor consultou (código interno e/ou CNPJ). Quando o
   * número do popup não bate com o esperado, é aqui que se vê se a empresa tem
   * segunda identidade.
   */
  chaves: string[];
  /** Janela EFETIVA (pode diferir da pedida — ver `janela_clampada`). */
  de: string | null;
  ate: string | null;
  /** `true` = o servidor recuou o `de` até o teto dele. Não corte em silêncio. */
  janela_clampada: boolean;
  total_eventos: number;
  /** `true` = há mais eventos do que o teto do servidor; os blocos são o recorte. */
  truncado: boolean;
  blocos: IHistoricoBloco[];
}

export interface IHistoricoUnidadeParams {
  bot: string;
  codi_emp: string;
  de?: string;
  ate?: string;
}

/**
 * A linha do tempo de UMA empresa num bot — TODOS os desfechos, inclusive `ok` e
 * `skip`.
 *
 * NÃO é `getAuditoriaErros` com filtro: aquela rota só traz desfecho RUIM, e
 * medido em produção a empresa com 14 eventos `skip` recebia ZERO dela. Esta é a
 * rota própria do monitoramento (PR #89), por isso existe.
 *
 * SOB DEMANDA, nunca embutida no `/resumo`: medido, embutir o histórico somaria
 * ~1,4 MB a todo carregamento de uma tela que já pesa ~2,5 MB, para um dado que o
 * usuário só vê ao abrir uma linha. Quente, uma chamada custa ~150-300 ms.
 *
 * `codi_emp` vai CRU (a grafia que a tela tem em mãos): é o servidor que resolve
 * grafia e identidade — ver o contrato de identidade de empresa.
 */
export const getHistoricoDaUnidade = async (
  params: IHistoricoUnidadeParams,
): Promise<IHistoricoUnidade> => {
  const res = await api.get<IEnvelope<IHistoricoUnidade>>(`${BASE}/auditoria/historico`, {
    params,
  });
  return res.data.data;
};

// ── Screenshot (LGPD: proxy token-gated, nunca URL crua do bucket) ───────────

/**
 * Monta a URL ABSOLUTA do proxy de screenshot (prefixada com o baseURL da instancia
 * `api` — a URL relativa nua apontaria para o host do front, nao do BFF). A resposta
 * e' binaria (image/png), fora do envelope JSON.
 *
 * ATENCAO no consumo: o endpoint exige Authorization e `<img src>` NAO envia header —
 * consumir via fetch autenticado + blob/objectURL (ou download autenticado), nunca
 * jogando a URL direto num `<img>`.
 */
export const urlScreenshot = (key: string, inline = true): string =>
  `${api.defaults.baseURL ?? ''}${BASE}/screenshot/view?key=${encodeURIComponent(key)}&inline=${inline}`;

/**
 * Busca o print AUTENTICADO (o `<img src>` cru não envia Authorization — ver nota do
 * `urlScreenshot`): devolve o Blob para o consumidor criar o objectURL (e REVOGAR ao
 * fechar). Rota ADMIN-ONLY (grupos 1/5) — usuário sem permissão leva 403, que a UI
 * trata com mensagem digna em vez de imagem quebrada.
 */
export const getScreenshotBlob = async (key: string): Promise<Blob> => {
  const res = await api.get<Blob>(`${BASE}/screenshot/view`, {
    params: { key, inline: true },
    responseType: 'blob',
  });
  return res.data;
};

// ── Notas: etapa 1 da esteira (100% Postgres) ────────────────────────────────

export interface INotasPendente {
  external_id: string;
  razao_social: string | null;
  cnpj: string | null;
  uf: string | null;
  divergencia_notas: number;
  valor_divergente: number;
}

/** Caminho preferido: usuario COM visao de carteira (conferencia fiscal). */
export interface INotasConferencia {
  fonte: 'conferencia';
  competencia: string;
  empresas_ativas: number;
  pendentes: number;
  com_aviso: number;
  conferidas: number;
  sem_movimento: number;
  pendentes_lista: INotasPendente[];
}

/** Fallback de contagem, com recorte por carteira. */
export interface INotasContagem {
  fonte: 'contagem';
  competencia: string;
  empresas: number;
  notas: number;
  por_tipo: Record<string, { notas: number; empresas: number }>;
  empresas_lista: string[];
  ultima_carga: string | null;
  cortada: boolean;
}

export type INotasImportacao = INotasConferencia | INotasContagem;

export const getNotasImportacao = async (params: {
  competencia?: string;
  limite_empresas?: number;
}): Promise<INotasImportacao> => {
  const res = await api.get<IEnvelope<INotasImportacao>>(`${BASE}/notas/importacao`, {
    params: comNoCache(params),
  });
  return res.data.data;
};

// ── Movimento (faturamento) por competencia ──────────────────────────────────

/**
 * Empresas COM movimento na competencia: nota de saida ou de servico lancada.
 * "Sem movimento" e' o complemento sobre o universo da area — ver `painelDoNo`.
 */
export interface IMovimento {
  competencia: string;
  com_movimento: string[];
  total_com_movimento: number;
  por_fonte: Record<string, number>;
  ultima_carga: string | null;
}

export const getMovimento = async (competencia: string): Promise<IMovimento> => {
  const res = await api.get<IEnvelope<IMovimento>>(`${BASE}/movimento`, {
    params: comNoCache({ competencia }),
  });
  return res.data.data;
};

// ── Excel do fluxo (19/10) ───────────────────────────────────────────────────

/**
 * Baixa o .xlsx de um fluxo. As linhas vão prontas no corpo (ver
 * `utils/exportarFluxo`): o backend só formata. Timeout próprio porque o fluxo
 * grande passa de 10 mil linhas no detalhe.
 */
export const baixarExcelDoFluxo = async (corpo: PlanilhaDoFluxo): Promise<Blob> => {
  const res = await api.post<Blob>(`${BASE}/exportar-fluxo`, corpo, {
    responseType: 'blob',
    timeout: 5 * 60 * 1000,
  });
  return res.data;
};

// ── Análise BOT × DIA (19/10, aba Análise recriada) ──────────────────────────

/** Um bot num dia (data local de São Paulo). Dias sem nada não vêm. */
export interface IBotDia {
  dia: string;
  /** Empresas distintas com alguma tentativa no dia. */
  empresas: number;
  /**
   * Pelo ÚLTIMO desfecho do dia, na régua do Processos: `ok` ou `skip` "feito"
   * (sem movimento, já transmitido) é concluída; falha, retry, aguarda humano e
   * `skip` de bloqueio são falha; `skip` "sem trabalho" é pendente.
   */
  concluidas: number;
  /** Das concluídas, quantas foram por `skip` "feito" (nada a fazer). */
  puladas?: number;
  /** Terminaram o dia em falha. */
  so_falha: number;
  pendentes: number;
  /** Todas as tentativas (retry incluso) — o sinal de retentativa em massa. */
  tentativas: number;
  tempo_medio_ok_ms: number | null;
  /** Soma da duração de todas as tentativas (tempo de máquina). */
  tempo_total_ms: number;
  empresas_por_hora: number | null;
  primeira: string | null;
  ultima: string | null;
  /** Execuções do bot no dia — GLOBAIS (não recortadas pela carteira). */
  execucoes: number;
  segundos_execucao: number;
  inicio_exec: string | null;
  fim_exec: string | null;
  ok_codis: string[];
  falha_codis: string[];
  pendente_codis?: string[];
  /** Todas as empresas tocadas no dia (cruzamento com movimento e analista). */
  codis: string[];
  /**
   * `[tentativas, tempo_total_ms, tempo_ok_ms, n_ok]` por empresa, NA ORDEM de
   * `codis`. A tela refaz tempo, ritmo e tentativas sobre o mesmo recorte das
   * contagens. Ausente no monitoramento antigo.
   */
  metricas?: Array<[number, number, number, number]>;
}

export interface IBotPeriodo {
  bot: string;
  tipo: string | null;
  totais: {
    empresas: number;
    concluidas: number;
    /**
     * Listas do PERÍODO pelo último desfecho de cada empresa — o mesmo status
     * do cartão do Processos. Ausentes no monitoramento antigo.
     */
    puladas?: number;
    falhas?: number;
    pendentes?: number;
    ok_codis?: string[];
    falha_codis?: string[];
    pendente_codis?: string[];
    codis?: string[];
    tentativas: number;
    so_falha_dias: number;
    dias_com_execucao: number;
    dias_com_conclusao: number;
    tempo_medio_ok_ms: number | null;
    tempo_total_ms: number;
    empresas_por_hora: number | null;
    tentativas_por_empresa: number | null;
  };
  dias: IBotDia[];
}

export interface IBotsPorDiaResposta {
  periodo: { date_init: string; date_end: string };
  /** Todos os dias da janela, em ordem. */
  dias: string[];
  fuso: string;
  recorte_carteira: boolean;
  execucoes_escopo: string;
  /** `"processos"` quando a conclusão segue a régua do Processos. */
  regra_conclusao?: string;
  carteira_restrita?: boolean;
  bots: IBotPeriodo[];
}

export const getBotsPorDia = async (periodo: IPeriodoParams = {}): Promise<IBotsPorDiaResposta> => {
  const res = await api.get<IEnvelope<IBotsPorDiaResposta>>(`${BASE}/bots-por-dia`, {
    params: comNoCache(periodo),
  });
  return res.data.data;
};

// ── Cobertura por no e funil por area ────────────────────────────────────────

/**
 * Uma contagem COM o seu denominador declarado.
 *
 * `base` e' o ROTULO do denominador ("empresas da area", "empresas que entraram
 * no bot", "empresas que entraram nesta etapa"). Ele viaja junto com o numero de
 * proposito: os tres niveis desta tela tem denominadores DIFERENTES, e um
 * percentual sem denominador explicito e' a origem da familia de bugs desta aba.
 * A interface e' obrigada a dizer sobre o que o numero e'.
 *
 * `quantidade === empresas.length` por construcao no backend — contagem e lista
 * nascem do mesmo array (a regra de ouro). `truncada` sinaliza a UNICA excecao.
 *
 * `percentual === null` = sem denominador confiavel; a tela exibe o aviso, nunca
 * um numero (nem 0%, que seria indistinguivel de "ninguem passou").
 */
export interface IFatiaCobertura {
  quantidade: number;
  percentual: number | null;
  /** Rotulo do denominador — SEMPRE exibido junto do percentual. */
  base: string;
  denominador: number | null;
  empresas: string[];
  listadas: number;
  truncada: boolean;
}

/** Uma etapa do funil de um bot. */
export interface IEtapaFunil {
  modulo_id: number;
  /** Posicao na sequencia (1-based) — a ordem do funil. */
  ordem: number;
  ultima: boolean;
  /** Quem entrou nesta etapa (denominador das tres fatias abaixo). */
  entraram: IFatiaCobertura;
  /**
   * Quem concluiu ESTA etapa E apareceu na SEGUINTE. Na ultima etapa, quem
   * concluiu — passar nela e' chegar ao final.
   */
  avancaram: IFatiaCobertura;
  /**
   * Quem entrou e NAO avancou — "onde as empresas ainda nao passaram". Inclui
   * tanto quem travou quanto quem concluiu a etapa e sumiu do fluxo.
   *
   * IDENTIDADE (garantida no backend e reconferida no BFF):
   *   entraram.quantidade === avancaram.quantidade + pararam.quantidade
   */
  pararam: IFatiaCobertura;
  /** Subconjunto de `pararam` com evento `falhou` e nenhum `concluido`. */
  falharam: IFatiaCobertura;
}

export interface IFunilBot {
  bot: string;
  entraram_no_bot: number;
  etapas: IEtapaFunil[];
  chegaram_ao_final: IFatiaCobertura;
}

/** NIVEL MICRO: um bot dentro da area. */
export interface IMicroCobertura {
  bot: string;
  /** Denominador = o MESMO da area-mae (nos comparaveis entre si). */
  cobertura: IFatiaCobertura;
  entraram: IFatiaCobertura;
  funil: IFunilBot;
}

/**
 * NIVEL GRUPO (21/09): o no' que a esteira macro desenha.
 *
 * Ate aqui o percentual so' existia por AREA e por BOT, e o no' do board macro e'
 * um GRUPO — entao todo grupo com != 1 bot ficava sem numero ("—") mesmo com
 * centenas de empresas rodando ("Transmissao dos Impostos LP/SN"). O front se
 * recusava a somar de proposito: somar as coberturas dos bots contaria DUAS VEZES
 * a empresa que passa em dois bots do mesmo grupo, e a informacao para deduplicar
 * (QUEM passou, nao quantos) so' existe no backend.
 *
 * Agora o mon publica a uniao deduplicada por grupo, e a bolha volta a ter numero.
 */
export interface IGrupoCobertura {
  grupo_id: string | null;
  nome: string | null;
  /** Bots que compoem o grupo — a uniao saiu destes. */
  bots: string[];
  /** Denominador = o MESMO da area-mae (nos comparaveis entre si). */
  cobertura: IFatiaCobertura;
  entraram: IFatiaCobertura;
  /**
   * COBERTURA POR ITEM (25/09) — o percentual do grupo que NAO processa empresa.
   *
   * Os 4 bots de "Solicitacoes Clientes DP" iteram TICKETS (`SOL-2493`): 27
   * chaves em producao, nenhuma cliente. Com o universo da area como denominador
   * eles ficavam em `·` ("sem execucao") tendo processado 27 coisas — o
   * denominador e' que estava errado, nao o numero.
   *
   * O denominador aqui sao os PROPRIOS itens que entraram ("18 de 27
   * solicitacoes"). `null` quando o grupo TEM empresa (a pergunta ja' tem
   * resposta certa) ou quando nao ha' item — ausencia nunca vira 0%.
   */
  cobertura_itens?: IFatiaCobertura | null;
  /**
   * ALGUM bot deste grupo reporta PASSAGEM POR MÓDULO? (22/09)
   *
   * A bolha do nó conta `bot_modulo_eventos`; o rodapé conta `bot_unit_result`.
   * Bot que não declara catálogo de módulos só fecha unidade — medido em produção:
   * `BOT_APURACAO_LUCRO_PRESUMIDO` tem **0 eventos e 348 unidades**, e
   * `BOT_APURACAO_FISCAL`, 0 e 9030.
   *
   * Sem este campo, esses nós exibiam o mesmo estado de um nó genuinamente parado —
   * "sem execução no período" com 348 empresas na linha de baixo. As duas leituras
   * estavam certas sobre coisas diferentes.
   *
   * OPCIONAL: durante a janela de deploy o mon ainda não publica. Ausente = não sei,
   * e a tela mantém o comportamento anterior.
   */
  reporta_modulos?: boolean;
}

/** NIVEL MACRO: uma area (departamento). */
export interface IAreaCobertura {
  departamento_id: string | null;
  nome: string;
  /** Regime efetivo da area (heranca departamento -> grupo -> bot); null = sem filtro. */
  regime: string | null;
  /**
   * Bots da area declaram regimes DIVERGENTES: nao ha universo unico, e a area
   * fica sem filtro em vez de escolher arbitrariamente qual metade fica errada.
   * A tela deve dizer isso — o percentual e' sobre TODAS as vigentes.
   */
  regime_ambiguo: boolean;
  bots: string[];
  /** O DENOMINADOR da area, ja' recortado pela carteira do usuario. */
  universo: number;
  /**
   * O universo NOMEADO (09/10) — os mesmos `codi_emp` que `universo` conta.
   *
   * Existe para a tela dizer QUAIS empresas do departamento nao apareceram em
   * agrupamento nenhum. Sem ele a secao "nao rodaram" exibia o numero e nao os
   * codigos ("2 nao rodaram" sem mostrar quais), e auditoria que nao se
   * consegue auditar nao serve. Medido: Fiscal LP 346 no universo contra 344
   * na uniao dos grupos; Fluxo Municipal 334 sem nome; Contabil 692 de 692.
   *
   * Ja' vem RECORTADO pela carteira (bff): o usuario restrito so' recebe os
   * codigos que ele pode ver — mesma mecanica de `cobertura` e `entraram`.
   *
   * Opcional porque um BFF anterior ao contrato nao o envia: nesse caso a
   * secao volta a nomear so' quem aparece nos agrupamentos, que e' o
   * comportamento de antes.
   */
  universo_empresas?: string[];
  /** O "X %" que vai ao lado do nome da area. Conta quem CONCLUIU. */
  cobertura: IFatiaCobertura;
  /**
   * Quem ENTROU em algum bot da area, no MESMO denominador da `cobertura`.
   *
   * Existe para a area poder distinguir os dois motivos de marcar 0%, que sao
   * operacionalmente opostos e ate 18/09 eram impressos igual:
   *   (a) `entraram` 0     — ninguem entrou, nao ha trabalho no periodo;
   *   (b) `entraram` > 0   — entraram e TRAVARAM (nenhuma concluiu).
   *
   * Era o bug relatado: area em "0% de 695" com o no' dela exibindo 21 empresas.
   * `cobertura <= entraram` sempre — quem concluiu necessariamente entrou.
   *
   * OPCIONAL no tipo de proposito: durante a janela de deploy o front novo fala
   * com o BFF antigo, que ainda nao publica o campo. Tratado como ausencia (sem
   * ressalva), nunca como zero — zero afirmaria "ninguem entrou".
   */
  entraram?: IFatiaCobertura;
  /**
   * Empresas vigentes SEM regime cadastrado, EXCLUIDAS do denominador quando a
   * area filtra por regime. Nunca somadas em silencio a um regime qualquer — a
   * tela exibe o contador para o numero ser auditavel.
   */
  sem_regime_definido: number;
  /**
   * Os GRUPOS da area, cada um com a uniao deduplicada dos seus bots.
   *
   * OPCIONAL de proposito: durante a janela de deploy o front novo conversa com
   * um BFF/mon que ainda nao publica o campo. Ausencia e' ausencia — a bolha
   * volta ao "—" explicado, nunca a um zero inventado.
   */
  grupos?: IGrupoCobertura[];
  micro: IMicroCobertura[];
}

export interface ICoberturaResposta {
  /** FALSE = espelho indisponivel: percentual NAO confiavel, nao renderizar numero. */
  espelho_disponivel: boolean;
  espelho_idade_s?: number | null;
  areas: IAreaCobertura[];
  periodo: { date_init: string | null; date_end: string | null };
  /** Payload truncado + usuario restrito: o recorte foi RECUSADO (nunca amostra). */
  recorte_indisponivel?: boolean;
  carteira_restrita?: boolean;
}

export const getCobertura = async (periodo: IPeriodoParams = {}): Promise<ICoberturaResposta> => {
  const res = await api.get<IEnvelope<ICoberturaResposta>>(`${BASE}/cobertura`, {
    params: comNoCache(periodo),
  });
  return res.data.data;
};

// ── Gargalo (Fase 2.4) e linha do tempo (Fase 2.3) ───────────────────────────

/**
 * Tempo de UMA etapa no periodo.
 *
 * ⚠️ O QUE `media_ms` MEDE, E O QUE NAO MEDE
 * A media sai SO' dos eventos `concluido`. Medido em producao (17/09/2026): o
 * evento `iniciado` tem zero duracao preenchida (respeita o model), mas o filtro
 * existe assim mesmo porque (a) `falhou` TEM duracao cheia — 2.109 eventos — e
 * mede outra coisa, e (b) uma regressao no produtor voltaria a duplicar o numero
 * em silencio. Ha teste no backend que quebra se o filtro sumir.
 *
 * `media_falha_ms` e' "quanto tempo ate desistir", medido A' PARTE de proposito:
 * somado a' media de sucesso, uma etapa que passa a falhar RAPIDO faria o tempo
 * medio MELHORAR — o painel diria que ela melhorou no dia em que quebrou.
 */
export interface IEtapaTempo {
  modulo_id: number;
  /** Quantos `concluido` entraram na conta (0 = etapa que so' falhou). */
  amostras: number;
  /** Media dos `concluido`; null quando nao houve conclusao no periodo. */
  media_ms: number | null;
  p50_ms: number | null;
  /** O p95 importa mais que a media: a cauda e' onde mora quem travou. */
  p95_ms: number | null;
  max_ms: number | null;
  /** TODAS as falhas do periodo — contagem de ocorrencia. */
  falhas: number;
  /**
   * Sobre quantas o tempo abaixo foi medido. Pode ser MENOR que `falhas` (falha sem
   * duracao gravada): os dois numeros descrevem populacoes diferentes e a tela nao
   * pode exibi-los como se fossem a mesma.
   */
  falhas_com_duracao: number;
  /** Tempo ate falhar — NUNCA somado a `media_ms`. */
  media_falha_ms: number | null;
  p95_falha_ms: number | null;
}

/** Uma empresa parada numa etapa ha' mais tempo — "quem precisa de atencao". */
export interface IEmpresaParada {
  codi_emp: string;
  modulo_id: number;
  /** O `ts` do `iniciado` que continua aberto (ISO). */
  desde: string;
  parada_s: number;
  bot: string | null;
  exec_id: string | null;
}

/** Quanta empresa cada etapa acumula AGORA — reusa a quantidade da 2.2. */
export interface IConcentracaoEtapa {
  modulo_id: number;
  quantidade: number;
  percentual: number | null;
}

/** Um ponto da serie "media da etapa X no dia D" (etapas demorando mais). */
export interface ITendenciaPonto {
  dia: string;
  modulo_id: number;
  amostras: number;
  media_ms: number;
}

export interface IGargaloResposta {
  espelho_disponivel: boolean;
  espelho_idade_s: number | null;
  universo: number;
  tempo_por_etapa: IEtapaTempo[];
  concentracao: IConcentracaoEtapa[];
  paradas: IEmpresaParada[];
  /**
   * TRUE = o ranking de paradas saiu de uma AMOSTRA: alguma etapa tinha mais
   * empresas que o teto do drill-down da 2.2, entao a empresa ha mais tempo parada
   * pode nao estar na lista. A tela deve dizer isso em vez de apresentar o ranking
   * como definitivo.
   */
  paradas_amostradas?: boolean;
  paradas_teto?: number;
  tendencia: ITendenciaPonto[];
  periodo: { date_init: string | null; date_end: string | null };
  /** Contrato: sempre "concluido". A tela pode exibir o que o numero mede. */
  duracao_filtrada_por: string;
  falhas_medidas_separadamente: boolean;
  /**
   * "global" = o tempo por etapa NAO e' recortado pela carteira (e' propriedade
   * do BOT, nao das empresas). A tela DEVE dizer isso ao usuario restrito, senao
   * ele conclui que aquele tempo descreve as empresas dele.
   */
  tempo_escopo?: string;
  carteira_restrita?: boolean;
  /**
   * TRUE = a concentracao NAO pode ser recortada para a carteira do usuario. A tela
   * deve avisar, nunca renderizar "zero empresas paradas" — sao coisas diferentes.
   */
  concentracao_indisponivel?: boolean;
  medicao_ms?: { tempo: number; tendencia: number; total: number };
}

export const getGargalo = async (periodo: IPeriodoParams = {}): Promise<IGargaloResposta> => {
  const res = await api.get<IEnvelope<IGargaloResposta>>(`${BASE}/gargalo`, {
    params: comNoCache(periodo),
  });
  return res.data.data;
};

/** Uma linha da comparacao entre duas datas. */
export interface ILinhaComparacao {
  /** "nao_entrou" | "etapa-<id>" | "concluiu". */
  chave: string;
  modulo_id: number | null;
  quantidade_a: number;
  quantidade_b: number;
  percentual_a: number | null;
  percentual_b: number | null;
  /** Subtracao de CONTAGENS — exata. */
  variacao_quantidade: number;
  /**
   * Diferenca de dois percentuais ja' arredondados, em PONTOS PERCENTUAIS. Nao e'
   * "variacao percentual" (que sobre base 1 produziria "aumentou 300%").
   */
  variacao_pp: number | null;
}

/**
 * "Como estava no dia X versus no dia Y".
 *
 * ⚠️ A POSICAO e' historica de verdade (`bot_modulo_eventos` e' append-only), mas
 * o DENOMINADOR NAO: o espelho de vigencia e' um snapshot sobrescrito, entao "o
 * universo do dia 5" nao existe. As duas datas usam o cadastro de HOJE. A tela e'
 * OBRIGADA a exibir `aviso_universo` — esconder isso produz um numero que fecha e
 * descreve outra coisa.
 */
export interface IComparacaoResposta {
  rotulo_a: string;
  rotulo_b: string;
  universo_a: number;
  universo_b: number;
  linhas: ILinhaComparacao[];
  data_a: string;
  data_b: string;
  espelho_disponivel: boolean;
  /** Sempre "hoje" — o universo historico nao existe. */
  universo_referencia: string;
  aviso_universo: string;
  /** Ate onde o historico alcanca (ISO); null se nao ha evento nenhum. */
  historico_desde: string | null;
  fora_do_historico: { a: boolean; b: boolean };
  /** Usuario restrito: contagens agregadas nao sao recortaveis por carteira. */
  comparacao_indisponivel?: boolean;
  motivo_indisponivel?: string;
  carteira_restrita?: boolean;
}

export const getComparacaoHistorica = async (params: {
  data_a: string;
  data_b: string;
  rotulo_a?: string;
  rotulo_b?: string;
}): Promise<IComparacaoResposta> => {
  const res = await api.get<IEnvelope<IComparacaoResposta>>(`${BASE}/historico/comparar`, {
    params: comNoCache(params),
  });
  return res.data.data;
};

/** Um instante da serie de evolucao: quantas empresas em cada categoria. */
export interface ISeriePonto {
  quando: string;
  universo: number;
  /** {"nao_entrou": N, "etapa-1": N, ..., "concluiu": N} — soma === universo. */
  quantidades: Record<string, number>;
}

export interface ISerieResposta {
  pontos: ISeriePonto[];
  passos: number;
  universo_referencia: string;
  historico_desde: string | null;
  duracao_ms?: number;
  /** Custo por ponto — a medicao que decide se falta indice em `ts`. */
  ms_por_ponto?: number;
  serie_indisponivel?: boolean;
  motivo_indisponivel?: string;
  carteira_restrita?: boolean;
}

export const getSerieHistorica = async (params: {
  date_init: string;
  date_end: string;
  passos?: number;
}): Promise<ISerieResposta> => {
  const res = await api.get<IEnvelope<ISerieResposta>>(`${BASE}/historico/serie`, {
    params: comNoCache(params),
  });
  return res.data.data;
};

/**
 * Ate onde o historico alcanca. A UI usa para DESABILITAR datas anteriores: pedir
 * uma data antes do primeiro evento devolveria "todo mundo em nao_entrou", numero
 * que fecha a invariante e mente (as empresas nao estavam paradas — a colecao e'
 * que nao existia).
 */
export interface IAlcanceResposta {
  historico_desde: string | null;
}

export const getAlcanceHistorico = async (): Promise<IAlcanceResposta> => {
  const res = await api.get<IEnvelope<IAlcanceResposta>>(`${BASE}/historico/alcance`, {
    params: comNoCache({}),
  });
  return res.data.data;
};
